import { createContext, type ReactNode, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { ApiError, AUTH_INVALID_EVENT } from "@/lib/api/client";
import type { User } from "@/lib/api/schemas";
import { api } from "@/lib/api/services";
import { adoptAccountLanguage } from "@/lib/language";
import { clearSessionCookies } from "./session-cookies";

type AuthState =
  | { status: "unknown"; user: null; error: unknown }
  | { status: "unauthenticated"; user: null; error: null }
  | { status: "authenticated"; user: User; error: null };

type LoginInput = { email: string; password: string; remember: boolean };
type SignupInput = { email: string; password: string; inviteCode: string; language: string };

type AuthContextValue = AuthState & {
  login: (input: LoginInput) => Promise<User>;
  signup: (input: SignupInput) => Promise<User>;
  loginWithTwoFactor: (preAuthToken: string, code: string, remember: boolean) => Promise<User>;
  logout: () => Promise<void>;
  refreshSession: () => Promise<User | null>;
};

/** Thrown by `login` when the account has 2FA enabled; the caller then collects a code and calls `loginWithTwoFactor`. */
export class TwoFactorRequiredError extends Error {
  readonly preAuthToken: string;

  constructor(preAuthToken: string) {
    super("two_factor_required");
    this.name = "TwoFactorRequiredError";
    this.preAuthToken = preAuthToken;
  }
}

function extractPreAuthToken(result: unknown) {
  if (!result || typeof result !== "object") return null;
  const record = result as Record<string, unknown>;
  const token = record["pre_auth_token"] ?? record["preAuthToken"];
  return typeof token === "string" && token ? token : null;
}

const AuthContext = createContext<AuthContextValue | null>(null);

function isUnauthenticatedError(error: unknown) {
  return error instanceof ApiError && (error.status === 401 || (error.status === 403 && error.code.toLowerCase().includes("not authenticated")));
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<AuthState>({ status: "unknown", user: null, error: null });

  const generation = useRef(0);
  const authRequest = useRef<AbortController | null>(null);
  const logoutFlight = useRef<Promise<void> | null>(null);
  const beginAuthRequest = useCallback(() => {
    authRequest.current?.abort();
    const controller = new AbortController();
    authRequest.current = controller;
    return { current: ++generation.current, signal: controller.signal };
  }, []);

  const restoreSession = useCallback(async (signal?: AbortSignal) => {
    if (logoutFlight.current) return null;
    const request = beginAuthRequest();
    const current = request.current;
    const requestSignal = signal ? AbortSignal.any([signal, request.signal]) : request.signal;
    try {
      const user = await api.getUser(requestSignal);
      if (signal?.aborted || current !== generation.current) return null;
      setState({ status: "authenticated", user, error: null });
      return user;
    } catch (error) {
      if (signal?.aborted || current !== generation.current) return null;
      if (isUnauthenticatedError(error)) {
        setState({ status: "unauthenticated", user: null, error: null });
        return null;
      }
      // A transient failure (network/timeout/5xx/invalid response) while refreshing an
      // already-authenticated session must not evict that session — only an explicit
      // 401/403 above may do that. Bootstrap (state not yet authenticated) still falls
      // back to "unknown" with the error, unchanged.
      setState((previous) => (previous.status === "authenticated" ? previous : { status: "unknown", user: null, error }));
      throw error;
    }
  }, [beginAuthRequest]);

  useEffect(() => {
    const controller = new AbortController();
    void restoreSession(controller.signal).catch(() => undefined);
    return () => controller.abort();
  }, [restoreSession]);

  // A device without an explicit language choice follows the account language saved in settings.
  const accountLanguage = state.status === "authenticated" ? state.user.lang : undefined;
  useEffect(() => { adoptAccountLanguage(accountLanguage); }, [accountLanguage]);

  useEffect(() => {
    const invalidate = () => { generation.current += 1; authRequest.current?.abort(); setState({ status: "unauthenticated", user: null, error: null }); };
    window.addEventListener(AUTH_INVALID_EVENT, invalidate);
    return () => window.removeEventListener(AUTH_INVALID_EVENT, invalidate);
  }, []);

  const login = useCallback(async (input: LoginInput) => {
    const { current, signal } = beginAuthRequest();
    const result = await api.login(input.email, input.password, input.remember, signal);
    if (current !== generation.current) throw new DOMException("Session changed", "AbortError");
    const preAuthToken = extractPreAuthToken(result);
    if (preAuthToken) throw new TwoFactorRequiredError(preAuthToken);
    if (current !== generation.current) throw new DOMException("Session changed", "AbortError");
    const user = await api.getUser(signal);
    if (current !== generation.current) throw new DOMException("Session changed", "AbortError");
    setState({ status: "authenticated", user, error: null });
    return user;
  }, [beginAuthRequest]);

  const loginWithTwoFactor = useCallback(async (preAuthToken: string, code: string, remember: boolean) => {
    const { current, signal } = beginAuthRequest();
    await api.loginWithTwoFactor(preAuthToken, code, remember, signal);
    if (current !== generation.current) throw new DOMException("Session changed", "AbortError");
    const user = await api.getUser(signal);
    if (current !== generation.current) throw new DOMException("Session changed", "AbortError");
    setState({ status: "authenticated", user, error: null });
    return user;
  }, [beginAuthRequest]);

  const signup = useCallback(async (input: SignupInput) => {
    const { current, signal } = beginAuthRequest();
    await api.signup(input.email, input.password, input.inviteCode, input.language, signal);
    if (current !== generation.current) throw new DOMException("Session changed", "AbortError");
    const user = await api.getUser(signal);
    if (current !== generation.current) throw new DOMException("Session changed", "AbortError");
    setState({ status: "authenticated", user, error: null });
    window.localStorage.removeItem("invite_code");
    return user;
  }, [beginAuthRequest]);

  const logout = useCallback(async () => {
    if (logoutFlight.current) return logoutFlight.current;
    const { current, signal } = beginAuthRequest();
    const operation = api.logout(signal).then(async () => {
      if (current !== generation.current || signal.aborted) throw new DOMException("Session changed", "AbortError");
      clearSessionCookies();
      try {
        await api.getUser(signal);
      } catch (error) {
        if (!isUnauthenticatedError(error)) throw error;
        if (current === generation.current) setState({ status: "unauthenticated", user: null, error: null });
        return;
      }
      throw new ApiError(409, "logout_not_confirmed");
    });
    logoutFlight.current = operation;
    try { await operation; }
    finally { if (logoutFlight.current === operation) logoutFlight.current = null; }
  }, [beginAuthRequest]);

  const refreshSession = useCallback(() => restoreSession(), [restoreSession]);
  const value = useMemo<AuthContextValue>(() => ({ ...state, login, loginWithTwoFactor, signup, logout, refreshSession }), [state, login, loginWithTwoFactor, signup, logout, refreshSession]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) throw new Error("AuthProvider is missing");
  return context;
}

export function useAuthenticatedUser() {
  const auth = useAuth();
  if (auth.status !== "authenticated") throw new Error("Authenticated user is unavailable");
  return { user: auth.user, refreshUser: auth.refreshSession };
}

/** Account currency of the signed-in user, or null (no session, or rendered outside AuthProvider). */
export function useAccountCurrency() {
  const context = useContext(AuthContext);
  return context?.status === "authenticated" ? context.user.currency : null;
}
